import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '@/services/api';
import { MSAService } from '@/types';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Input } from '@/components/ui/Input';
import CreditPlanManagement from '@/components/services/management/superadmin/CreditPlanManagement';
import { getServiceStatusBadge, getHealthBadge } from '@/utils/badgeUtils';
import { useDebounce } from '@/hooks/common';
import { useServices } from '@/hooks/services';

const ServiceCreditPlanPage: React.FC = () => {
  const {
    services,
    filteredServices,
    loading,
    error,
    setError,
    healthStatuses,
    fetchServices,
    searchQuery,
    setSearchQuery,
  } = useServices();

  const debouncedSearch = useDebounce(searchQuery, 500);
  const [selectedService, setSelectedService] = useState<MSAService | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [planCounts, setPlanCounts] = useState<Record<number, number>>({});

  const loadPlanCounts = useCallback(async (targetServices: MSAService[]) => {
    const result: Record<number, number> = {};
    await Promise.all(
      targetServices.map(async (s) => {
        try {
          const plans = await adminAPI.getServiceQuotaPlans(s.service_slug, true);
          // 모든 플랜을 카운트 (quota_type이 'api_calls'인 플랜도 포함)
          result[s.id] = (plans || []).length;
        } catch {
          result[s.id] = 0;
        }
      }),
    );
    setPlanCounts(result);
  }, []);

  useEffect(() => {
    // debouncedSearch 는 useServices 의 searchQuery 에서 이미 반영되므로,
    // 여기서는 단순히 의존성만 유지 (필요 시 추가 필터링에 사용 가능)
  }, [debouncedSearch]);

  // 서비스별 크레딧 플랜 개수 로드
  useEffect(() => {
    if (services.length > 0) {
      loadPlanCounts(services);
    }
  }, [services, loadPlanCounts]);

  const handleRefresh = async () => {
    await fetchServices();
    if (services.length > 0) {
      await loadPlanCounts(services);
    }
  };

  // totalCreditPlans는 현재 사용되지 않으므로 주석 처리
  // const totalCreditPlans = useMemo(
  //   () => Object.values(planCounts).reduce((sum, v) => sum + v, 0),
  //   [planCounts],
  // );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">서비스 요금제 관리</h2>
          <p className="text-sm text-gray-600 mt-1">
            모든 등록된 서비스에 대한 월정액 또는 이용횟수별 크레딧 기반 과금 플랜을 설정합니다.
          </p>
        </div>
      </div>

      {error && (
        <AlertMessage type="error" message={error} onClose={() => setError('')} />
      )}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <CardTitle>
              서비스 목록
              <span className="ml-2 text-sm text-gray-500">
                (총 서비스 {filteredServices.length}개)
              </span>
            </CardTitle>
          </div>
          <div className="flex items-center gap-3">
            <Input
              className="max-w-xs"
              placeholder="서비스 이름, 설명, 상태로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              새로고침
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-gray-500 py-8">서비스를 불러오는 중...</p>
          ) : filteredServices.length === 0 ? (
            <p className="text-center text-gray-500 py-8">등록된 서비스가 없습니다.</p>
          ) : (
            // 좁은 화면에서는 글자 단위로 꺾이지 않고 가로로 넘긴다(공통 Table 이 스크롤을 감싼다, #220)
            <Table className="min-w-[960px]">
              <TableHeader>
                <TableRow className="whitespace-nowrap">
                  <TableHead>ID</TableHead>
                  <TableHead>서비스 이름</TableHead>
                  <TableHead>버전</TableHead>
                  <TableHead>설명</TableHead>
                  <TableHead>등록 상태</TableHead>
                  <TableHead>현재 상태</TableHead>
                  <TableHead>크레딧 플랜 수</TableHead>
                  <TableHead className="text-right">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredServices.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell className="text-sm text-gray-600">
                      {service.id}
                    </TableCell>
                    <TableCell className="font-medium">{service.service_name}</TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {service.version || '-'}
                    </TableCell>
                    <TableCell className="min-w-[16rem] text-sm text-gray-600">
                      {service.service_description || '설명 없음'}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {getServiceStatusBadge(service.status)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {getHealthBadge(healthStatuses[service.id] || 'OFFLINE')}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {planCounts[service.id] ?? 0}개
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedService(service);
                          setOpenDialog(true);
                        }}
                      >
                        서비스 요금제 관리
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {selectedService && (
        <Dialog
          open={openDialog}
          onOpenChange={(open) => {
            setOpenDialog(open);
            if (!open) {
              setSelectedService(null);
            }
          }}
        >
          <DialogContent
            className="max-w-4xl max-h-[90vh] overflow-y-auto"
            onClose={() => {
              setOpenDialog(false);
              setSelectedService(null);
            }}
          >
            <DialogHeader>
              <DialogTitle>서비스 요금제 관리 - {selectedService.service_name}</DialogTitle>
            </DialogHeader>
            <CreditPlanManagement
              serviceSlug={selectedService.service_slug}
              serviceName={selectedService.service_name}
            />
            <DialogFooter className="pt-4 border-t mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpenDialog(false);
                  setSelectedService(null);
                }}
              >
                닫기
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ServiceCreditPlanPage;


